// The web avatar loader (ADR-0022 D12, web half).
//
// The wire carries a VERSION, never a URL; this module composes the fetch URL
// from (owner, version, rendition) against our own origin — the byte-for-byte
// port of the shared Kotlin `AvatarUrl` — and fetches it with the Bearer
// header an `<img src>` cannot carry (the same reason realtime uses a ticket:
// api-client.ts's WS note). The bytes become a blob: URL the renderer's
// sanitiser accepts, held in a bounded LRU whose eviction revokes the URL.
//
// A 404 — no photo, a stale version, or one this viewer may not see; the
// server keeps the three indistinguishable — is remembered briefly so a
// hidden face does not cost a request per repaint.

export type AvatarRendition = "thumb" | "full";

export interface AvatarRefLike {
  kind: "user" | "chat";
  ownerId: string;
  version: string;
  rendition: AvatarRendition;
}

/** The ONE URL builder — the port of the shared `AvatarUrl`. */
export function avatarUrl(apiBase: string, ref: AvatarRefLike): string {
  const base = apiBase.replace(/\/+$/, "");
  const kind = ref.kind === "user" ? "users" : "chats";
  return `${base}/api/v1/${kind}/${encodeURIComponent(ref.ownerId)}/avatar?v=${ref.version}&size=${ref.rendition}`;
}

const MISS_TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 96;

export class AvatarLoader {
  /** key → blob: URL, in insertion (≈ LRU) order. */
  private blobs = new Map<string, string>();
  private misses = new Map<string, number>();
  private inflight = new Map<string, Promise<string | null>>();

  private readonly apiBase: string;
  private readonly bearer: () => string | null;
  /** Called when a blob URL becomes available, so the shell can repaint. */
  private readonly onLoaded: () => void;
  private readonly doFetch: typeof fetch;

  constructor(
    apiBase: string,
    bearer: () => string | null,
    onLoaded: () => void,
    doFetch: typeof fetch = (...args) => fetch(...args),
  ) {
    this.apiBase = apiBase;
    this.bearer = bearer;
    this.onLoaded = onLoaded;
    this.doFetch = doFetch;
  }

  private key(ref: AvatarRefLike): string {
    return `${ref.kind}/${ref.ownerId}/${ref.version}/${ref.rendition}`;
  }

  /** The blob: URL if this loader already holds one — synchronous, for the
   *  pure view builder. Kicks off a fetch when it holds nothing. */
  peek(ref: AvatarRefLike): string | null {
    const key = this.key(ref);
    const hit = this.blobs.get(key);
    if (hit) {
      // refresh recency
      this.blobs.delete(key);
      this.blobs.set(key, hit);
      return hit;
    }
    const missedAt = this.misses.get(key);
    if (missedAt !== undefined && Date.now() < missedAt + MISS_TTL_MS) return null;
    void this.load(ref);
    return null;
  }

  async load(ref: AvatarRefLike): Promise<string | null> {
    const key = this.key(ref);
    const existing = this.blobs.get(key);
    if (existing) return existing;
    const flying = this.inflight.get(key);
    if (flying) return flying;

    const p = this.fetchBlob(ref, key);
    this.inflight.set(key, p);
    try {
      return await p;
    } finally {
      this.inflight.delete(key);
    }
  }

  private async fetchBlob(ref: AvatarRefLike, key: string): Promise<string | null> {
    const token = this.bearer();
    if (!token) return null;
    let res: Response;
    try {
      res = await this.doFetch(avatarUrl(this.apiBase, ref), {
        headers: { authorization: `Bearer ${token}`, accept: "image/jpeg" },
      });
    } catch {
      return null; // network hiccup: not a miss worth remembering
    }
    if (!res.ok) {
      this.misses.set(key, Date.now());
      return null;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    this.remember(key, url);
    this.onLoaded();
    return url;
  }

  private remember(key: string, url: string): void {
    this.blobs.set(key, url);
    while (this.blobs.size > MAX_ENTRIES) {
      const oldest = this.blobs.keys().next().value as string;
      const victim = this.blobs.get(oldest);
      this.blobs.delete(oldest);
      if (victim) URL.revokeObjectURL(victim);
    }
  }

  /** After the viewer's own privacy or a block changed. */
  forgetMisses(): void {
    this.misses.clear();
  }

  /** Sign-out: drop and revoke everything. */
  clear(): void {
    for (const url of this.blobs.values()) URL.revokeObjectURL(url);
    this.blobs.clear();
    this.misses.clear();
  }
}
